import { all, call, put, select, take, fork, cancel } from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';

import * as casePresenceActions from 'modules/casePresence/actions';
import { getAccessToken } from 'modules/auth/reducer';
import * as authActions from 'modules/auth/actions';
import { getUserId } from 'modules/user/reducer';
import { getSharedCableClient, resetSharedCableClient } from 'utils/sharedCableClient';
import { showCaseEditedToast } from 'utils/caseEditedToast';
import { ENABLE_CASE_ACTIVITY_CHANNEL } from 'constants/api';
import { convertStringToBoolean } from 'utils/common';

/**
 * Create a redux-saga eventChannel that bridges the shared cable client
 * subscription events into the saga.
 *
 * @method createPresenceChannel
 * @param  {Object} client   The shared cable client instance
 * @param  {string} token    The access token
 * @param  {string} caseId   The case/ticket ID to subscribe to
 * @return {Object}          A redux-saga eventChannel
 */
export function createPresenceChannel(client, token, caseId) {
    return eventChannel(emit => {
        client.subscribe(caseId, token, {
            received(data) {
                if (data.type === 'ticket_edited') {
                    emit({
                        type: 'ticket_edited',
                        caseId: data.caseId,
                        editedBy: data.editedBy || {},
                        editedCategory: data.editedCategory || '',
                        message: data.message || '',
                    });
                } else {
                    emit({
                        type: 'viewers_updated',
                        caseId: data.caseId,
                        viewers: data.viewers || [],
                    });
                }
            },
            connected() {
                emit({ type: 'connected', caseId });
            },
            disconnected() {
                emit({ type: 'disconnected', caseId });
            },
            rejected() {
                emit({ type: 'rejected', caseId });
                emit(END);
            },
        });

        return () => {
            client.unsubscribe(caseId);
        };
    });
}

/**
 * Saga that manages the lifecycle of a single case presence subscription.
 *
 * @method handleCasePresenceSubscription
 * @param  {Object} client           The shared cable client instance
 * @param  {Object} subscriptionTasks Shared map of active task references
 * @param  {string} caseId           The case ID to subscribe to
 */
export function* handleCasePresenceSubscription(client, subscriptionTasks, caseId) {
    const token = yield select(getAccessToken);

    if (!token) {
        yield put(
            casePresenceActions.subscribeCasePresence.error({ caseId, error: 'No auth token' })
        );
        return;
    }

    let channel;

    try {
        channel = yield call(createPresenceChannel, client, token, caseId);

        while (true) {
            const event = yield take(channel);

            if (event.type === 'viewers_updated') {
                yield put(
                    casePresenceActions.updateCaseViewers.success({
                        caseId: event.caseId,
                        viewers: event.viewers,
                    })
                );
            } else if (event.type === 'ticket_edited') {
                const currentUserId = yield select(getUserId);
                const editorUserId = event.editedBy?.user_id;
                if (String(editorUserId) !== String(currentUserId)) {
                    yield call(
                        showCaseEditedToast,
                        event.editedBy?.display_name,
                        event.editedCategory,
                        event.message
                    );
                }
            } else if (event.type === 'connected') {
                yield put(casePresenceActions.subscribeCasePresence.success({ caseId }));
            } else if (event.type === 'rejected') {
                yield put(
                    casePresenceActions.subscribeCasePresence.error({
                        caseId,
                        error: 'Subscription rejected',
                    })
                );
            }
        }
    } catch (error) {
        yield put(
            casePresenceActions.subscribeCasePresence.error({ caseId, error: error.message })
        );
    } finally {
        if (channel) {
            channel.close();
        }
        delete subscriptionTasks[caseId];
    }
}

/**
 * Watch for subscribe actions and fork a subscription handler for each case.
 * Checks task.isRunning() to allow re-subscription after disconnection.
 *
 * @method watchSubscribeCasePresence
 * @param  {Object} client           The shared cable client instance
 * @param  {Object} subscriptionTasks Shared map of active task references
 */
export function* watchSubscribeCasePresence(client, subscriptionTasks) {
    while (true) {
        const action = yield take(casePresenceActions.subscribeCasePresence.TRIGGER);
        const caseId = action?.payload?.caseId;

        if (caseId) {
            const existingTask = subscriptionTasks[caseId];

            if (!existingTask?.isRunning()) {
                subscriptionTasks[caseId] = yield fork(
                    handleCasePresenceSubscription,
                    client,
                    subscriptionTasks,
                    caseId
                );
            }
        }
    }
}

/**
 * Watch for unsubscribe actions and cancel the corresponding subscription task.
 *
 * @method watchUnsubscribeCasePresence
 * @param  {Object} subscriptionTasks Shared map of active task references
 */
export function* watchUnsubscribeCasePresence(subscriptionTasks) {
    while (true) {
        const action = yield take(casePresenceActions.unsubscribeCasePresence.TRIGGER);
        const caseId = action?.payload?.caseId;

        if (caseId) {
            if (subscriptionTasks[caseId]) {
                yield cancel(subscriptionTasks[caseId]);
                delete subscriptionTasks[caseId];
            }

            yield put(casePresenceActions.unsubscribeCasePresence.success({ caseId }));
        }
    }
}

/**
 * Watch for session-end events (inactivity auto-logout, explicit logout,
 * internal logout) and tear down every active presence subscription.
 *
 * When a user's session ends the ActionCable socket is not closed on its own,
 * so the backend never receives an `unsubscribed` event and the user keeps
 * being reported as viewing the case. Cancelling each subscription task runs
 * its cleanup, which unsubscribes from the cable channel and lets the backend
 * remove the user from the presence list immediately.
 *
 * @method watchSessionTeardown
 * @param  {Object} subscriptionTasks Shared map of active task references
 */
export function* watchSessionTeardown(subscriptionTasks) {
    while (true) {
        const action = yield take([
            authActions.setIsExpired.TRIGGER,
            authActions.logout.TRIGGER,
            authActions.internallyLogout.TRIGGER,
        ]);

        // `setIsExpired` also fires with a falsy payload on successful login;
        // only tear down when the session is actually expiring.
        const isSessionEnding =
            action.type !== authActions.setIsExpired.TRIGGER || Boolean(action?.payload);

        if (isSessionEnding) {
            const caseIds = Object.keys(subscriptionTasks);

            for (let i = 0; i < caseIds.length; i += 1) {
                const caseId = caseIds[i];
                const task = subscriptionTasks[caseId];

                if (task) {
                    yield cancel(task);
                    delete subscriptionTasks[caseId];
                }

                yield put(casePresenceActions.unsubscribeCasePresence.success({ caseId }));
            }
        }
    }
}

/**
 * Watch for token refresh events and notify the shared cable client
 * so it can rotate the ActionCable connection.
 *
 * @method watchTokenRefresh
 * @param  {Object} client The shared cable client instance
 */
export function* watchTokenRefresh(client) {
    while (true) {
        const action = yield take(authActions.fetchToken.SUCCESS);
        const newToken = action?.payload?.access_token;
        if (newToken) {
            client.updateToken(newToken);
        }
    }
}

/**
 * Root saga for the casePresence module.
 * Obtains the shared cable client and uses it for all subscriptions.
 * The client is shared across tabs via SharedWorker when available,
 * falling back to per-tab CableConsumer otherwise.
 */
export default function* casePresenceSaga() {
    if (!convertStringToBoolean(ENABLE_CASE_ACTIVITY_CHANNEL)) {
        return;
    }

    const subscriptionTasks = {};
    const client = getSharedCableClient();

    try {
        yield all([
            call(watchSubscribeCasePresence, client, subscriptionTasks),
            call(watchUnsubscribeCasePresence, subscriptionTasks),
            call(watchSessionTeardown, subscriptionTasks),
            call(watchTokenRefresh, client),
        ]);
    } finally {
        resetSharedCableClient();
    }
}
